/**
 * EvoIcons — EvoHR custom brand icon library
 *
 * Design spec:
 *  - 24x24 viewBox
 *  - 1.5 stroke width, round caps & joins
 *  - Uses `currentColor` so the icon inherits the surrounding text color
 *    (gold #D4A843 on dark surfaces, dark #111827 on light surfaces)
 *  - Minimal, geometric, clean lines — every icon belongs to the same family
 *  - Each icon is geometrically unique (no two confusable shapes)
 *
 * Usage:
 *   import { EmployeesIcon } from "@/components/icons/EvoIcons";
 *   <EmployeesIcon className="text-primary" size={24} />
 */
import React from "react";

export interface EvoIconProps {
  className?: string;
  size?: number;
  strokeWidth?: number;
  title?: string;
}

const Svg: React.FC<EvoIconProps & { children: React.ReactNode }> = ({
  className,
  size = 24,
  strokeWidth = 1.5,
  title,
  children,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={title ? "img" : "presentation"}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

/* ─────────────────────────  PLATFORM / NAVIGATION  ───────────────────────── */

export const EvoHRIcon: React.FC<EvoIconProps> = (p) => (
  // Abstract "E + H" mark: vertical stem, two crossbars with a node bridge
  <Svg {...p}>
    <path d="M5 4v16" />
    <path d="M5 6h7" />
    <path d="M5 12h6" />
    <path d="M5 18h7" />
    <circle cx="17" cy="12" r="2.25" />
    <path d="M11 12h3.75" />
    <path d="M19.25 12H21" />
  </Svg>
);

export const EvoBusinessIcon: React.FC<EvoIconProps> = (p) => (
  // Briefcase with three connected nodes above
  <Svg {...p}>
    <rect x="3" y="9" width="18" height="11" rx="2" />
    <path d="M9 9V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <circle cx="7" cy="4" r="1" />
    <circle cx="12" cy="3" r="1" />
    <circle cx="17" cy="4" r="1" />
    <path d="M7 4l5-1 5 1" />
  </Svg>
);

export const EvoHospitalityIcon: React.FC<EvoIconProps> = (p) => (
  // Building silhouette with a key card slot
  <Svg {...p}>
    <path d="M4 21V6l8-3 8 3v15" />
    <path d="M4 21h16" />
    <rect x="9" y="13" width="6" height="3" rx="0.5" />
    <path d="M8 9h2" />
    <path d="M14 9h2" />
  </Svg>
);

export const EvoAIIcon: React.FC<EvoIconProps> = (p) => (
  // Neural network: 3 input nodes → hidden node → 2 output nodes
  <Svg {...p}>
    <circle cx="4" cy="6" r="1.25" />
    <circle cx="4" cy="12" r="1.25" />
    <circle cx="4" cy="18" r="1.25" />
    <circle cx="12" cy="12" r="1.75" />
    <circle cx="20" cy="9" r="1.25" />
    <circle cx="20" cy="15" r="1.25" />
    <path d="M5.25 6l5.25 5.25" />
    <path d="M5.25 12h5.25" />
    <path d="M5.25 18l5.25-5.25" />
    <path d="M13.5 11l5.25-1.75" />
    <path d="M13.5 13l5.25 1.75" />
  </Svg>
);

/* ─────────────────────────────  HR & PEOPLE  ───────────────────────────── */

export const EmployeesIcon: React.FC<EvoIconProps> = (p) => (
  // Person silhouette with three data dots beside
  <Svg {...p}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 20c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" />
    <circle cx="18" cy="7" r="0.6" fill="currentColor" />
    <circle cx="20.5" cy="10" r="0.6" fill="currentColor" />
    <circle cx="18" cy="13" r="0.6" fill="currentColor" />
  </Svg>
);

export const OrgChartIcon: React.FC<EvoIconProps> = (p) => (
  // Hierarchy: one parent → three children
  <Svg {...p}>
    <rect x="9" y="3" width="6" height="4" rx="0.75" />
    <rect x="3" y="17" width="5" height="4" rx="0.75" />
    <rect x="9.5" y="17" width="5" height="4" rx="0.75" />
    <rect x="16" y="17" width="5" height="4" rx="0.75" />
    <path d="M12 7v4" />
    <path d="M5.5 17v-3h13v3" />
  </Svg>
);

export const OnboardingIcon: React.FC<EvoIconProps> = (p) => (
  // Person with an arrow arc indicating welcome / addition
  <Svg {...p}>
    <circle cx="10" cy="7" r="2.75" />
    <path d="M4.5 19.5c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" />
    <path d="M17.5 7.5h4" />
    <path d="M19.5 5.5v4" />
  </Svg>
);

export const PerformanceIcon: React.FC<EvoIconProps> = (p) => (
  // Ascending bars with a star above the tallest
  <Svg {...p}>
    <path d="M3 21h18" />
    <rect x="4.5" y="14" width="3" height="6" rx="0.5" />
    <rect x="10.5" y="10" width="3" height="10" rx="0.5" />
    <rect x="16.5" y="6" width="3" height="14" rx="0.5" />
    <path d="M18 2.5l0.65 1.45 1.6.18-1.2 1.07.32 1.55L18 5.95l-1.37.8.32-1.55-1.2-1.07 1.6-.18z" />
  </Svg>
);

export const RecruitmentIcon: React.FC<EvoIconProps> = (p) => (
  // Magnifying lens containing a person bust
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="M15.5 15.5L20 20" />
    <circle cx="10.5" cy="9" r="1.75" />
    <path d="M7.5 13.5c0-1.6 1.35-2.75 3-2.75s3 1.15 3 2.75" />
  </Svg>
);

export const TrainingIcon: React.FC<EvoIconProps> = (p) => (
  // Open book with a lightbulb above
  <Svg {...p}>
    <path d="M3 17V8l4.5-1L12 8.5 16.5 7 21 8v9l-4.5-1L12 17.5 7.5 16z" />
    <path d="M12 8.5v9" />
    <path d="M12 6.5c.9-1.1 2.4-1.1 3.3 0 .55.7.55 1.7 0 2.4l-1.65 1.6-1.65-1.6c-.55-.7-.55-1.7 0-2.4z" transform="translate(-3.65 -3.5)" />
  </Svg>
);

export const AttendanceIcon: React.FC<EvoIconProps> = (p) => (
  // Clock with a small check mark
  <Svg {...p}>
    <circle cx="11" cy="12" r="7.5" />
    <path d="M11 8v4l2.5 1.5" />
    <path d="M16 18.5l1.75 1.75L21 17" />
  </Svg>
);

export const LeavesIcon: React.FC<EvoIconProps> = (p) => (
  // Calendar with a wave line at the bottom
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="M6 17c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0" />
  </Svg>
);

export const PayrollIcon: React.FC<EvoIconProps> = (p) => (
  // Coin with circular cycle arrows
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 9.5v5" />
    <path d="M10.75 11h2c.7 0 1.25.55 1.25 1.25S13.45 13.5 12.75 13.5h-2.5" />
    <path d="M19 8a8 8 0 0 0-13.5-1.5" />
    <path d="M5 16a8 8 0 0 0 13.5 1.5" />
    <path d="M5.5 6.5V9H8" />
    <path d="M18.5 17.5V15H16" />
  </Svg>
);

/* ──────────────────────────────  BUSINESS  ────────────────────────────── */

export const CRMIcon: React.FC<EvoIconProps> = (p) => (
  // Two heads with an arc bridge — relationship symbol
  <Svg {...p}>
    <circle cx="7.5" cy="8" r="2.5" />
    <circle cx="16.5" cy="8" r="2.5" />
    <path d="M3.5 19c0-2.5 1.8-4.5 4-4.5s4 2 4 4.5" />
    <path d="M12.5 19c0-2.5 1.8-4.5 4-4.5s4 2 4 4.5" />
    <path d="M9.5 6.5c.7-.7 1.5-1.2 2.5-1.2s1.8.5 2.5 1.2" />
  </Svg>
);

export const FinanceIcon: React.FC<EvoIconProps> = (p) => (
  // Chart line over a coin
  <Svg {...p}>
    <path d="M3 17l4-4 3 3 4-5 4 4 3-3" />
    <path d="M3 21h18" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <path d="M6.5 5v3" />
  </Svg>
);

export const TasksIcon: React.FC<EvoIconProps> = (p) => (
  // Checklist with progress (one item half complete)
  <Svg {...p}>
    <rect x="3.5" y="4.5" width="4" height="4" rx="0.75" />
    <path d="M4.5 6.5l1 1 2-2" />
    <rect x="3.5" y="10.5" width="4" height="4" rx="0.75" />
    <path d="M4.5 12.5l1 1 2-2" />
    <rect x="3.5" y="16.5" width="4" height="4" rx="0.75" />
    <path d="M10 6.5h11" />
    <path d="M10 12.5h11" />
    <path d="M10 18.5h7" />
  </Svg>
);

export const DocumentsIcon: React.FC<EvoIconProps> = (p) => (
  // Document with a wax seal in the corner
  <Svg {...p}>
    <path d="M6 3h8l4 4v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    <path d="M14 3v4h4" />
    <circle cx="15.5" cy="16" r="2" />
    <path d="M14.25 17.5l-.75 2 2-1 2 1-.75-2" />
  </Svg>
);

export const ApprovalsIcon: React.FC<EvoIconProps> = (p) => (
  // Shield with checkmark
  <Svg {...p}>
    <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
    <path d="M8.5 12l2.5 2.5 4.5-5" />
  </Svg>
);

export const LogisticsIcon: React.FC<EvoIconProps> = (p) => (
  // Curving route with a pin at the end
  <Svg {...p}>
    <path d="M4 18c0-3 3-3 5-3s5 0 5-4-3-4-5-4-5 0-5-3" />
    <circle cx="4" cy="4" r="1.25" fill="currentColor" />
    <path d="M18 11.5c1.66 0 3 1.34 3 3 0 2.5-3 5-3 5s-3-2.5-3-5c0-1.66 1.34-3 3-3z" />
    <circle cx="18" cy="14.5" r="1" />
  </Svg>
);

/* ────────────────────────────  HOSPITALITY  ────────────────────────────── */

export const HotelIcon: React.FC<EvoIconProps> = (p) => (
  // Building grid with a key card hanging
  <Svg {...p}>
    <path d="M3 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
    <path d="M3 21h18" />
    <path d="M6 8h2M6 12h2M6 16h2M12 8h2M12 12h2M12 16h2" />
    <rect x="18" y="9" width="3" height="6" rx="0.5" />
    <path d="M19.5 9V7" />
  </Svg>
);

export const RestaurantIcon: React.FC<EvoIconProps> = (p) => (
  // Plate with fork & knife abstract
  <Svg {...p}>
    <circle cx="12" cy="14" r="6.5" />
    <circle cx="12" cy="14" r="3.5" />
    <path d="M5 3v5c0 .8.7 1.5 1.5 1.5S8 8.8 8 8V3" />
    <path d="M6.5 3v6.5" />
    <path d="M18 3c-1.5 0-2.5 1.5-2.5 3.5V9h2.5z" />
    <path d="M18 3v6.5" />
  </Svg>
);

export const ReservationsIcon: React.FC<EvoIconProps> = (p) => (
  // Calendar with a small bed silhouette
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="M6 17v-2.5h5v2.5" />
    <path d="M11 17v-2c0-.6.4-1 1-1h5c.6 0 1 .4 1 1v2" />
    <path d="M6 17h12" />
  </Svg>
);

export const HousekeepingIcon: React.FC<EvoIconProps> = (p) => (
  // Folded cloth with sparkles
  <Svg {...p}>
    <path d="M4 18l4-9h8l4 9z" />
    <path d="M4 18h16" />
    <path d="M8 18v-4h8v4" />
    <path d="M19 4l.5 1.2L20.7 5.7l-1.2.5L19 7.4l-.5-1.2L17.3 5.7l1.2-.5z" />
    <path d="M5 5l.4 1 .9.4-.9.4L5 7.8 4.6 6.8 3.7 6.4l.9-.4z" />
  </Svg>
);

export const FrontDeskIcon: React.FC<EvoIconProps> = (p) => (
  // Counter with a service bell
  <Svg {...p}>
    <path d="M3 20h18" />
    <path d="M5 20v-5h14v5" />
    <path d="M7 15c0-2.8 2.2-5 5-5s5 2.2 5 5" />
    <path d="M12 10V8" />
    <path d="M11 8h2" />
  </Svg>
);

export const KitchenIcon: React.FC<EvoIconProps> = (p) => (
  // Pan with a flame underneath
  <Svg {...p}>
    <path d="M3 11h14a3 3 0 0 1 0 6H7l-4-6z" />
    <path d="M17 14h3" />
    <path d="M9 8c0-1 .5-1.5 1-2-.2 1 .5 1.5 1 2.5s-.5 2-1.5 2c-.8 0-1.5-.7-1.5-1.5C8 8.5 8.5 8.5 9 8z" />
    <path d="M13 7c.3-.8.7-1 1-1.5-.1.8.4 1 .8 1.8.4.8-.3 1.7-1.2 1.7-.7 0-1.2-.6-1.1-1.2C12.6 7.3 12.7 7.4 13 7z" />
  </Svg>
);

/* ────────────────────────────  INDUSTRIES  ─────────────────────────────── */

export const HealthcareIcon: React.FC<EvoIconProps> = (p) => (
  // Plus cross with a pulse line
  <Svg {...p}>
    <path d="M10 3h4v6h6v4h-6v6h-4v-6H4V9h6z" />
    <path d="M3 13h2l1.5-3 2 6 1.5-3h2" />
  </Svg>
);

export const RetailIcon: React.FC<EvoIconProps> = (p) => (
  // Storefront with awning
  <Svg {...p}>
    <path d="M3 9l2-4h14l2 4" />
    <path d="M3 9v11h18V9" />
    <path d="M3 9h18" />
    <path d="M10 20v-5h4v5" />
    <path d="M6 13h2v2H6z" />
  </Svg>
);

export const TechnologyIcon: React.FC<EvoIconProps> = (p) => (
  // Code brackets with a circuit dot in the middle
  <Svg {...p}>
    <path d="M8 5l-5 7 5 7" />
    <path d="M16 5l5 7-5 7" />
    <circle cx="12" cy="12" r="1.25" fill="currentColor" />
    <path d="M10.5 12h-2" />
    <path d="M15.5 12h-2" />
  </Svg>
);

export const FinancialServicesIcon: React.FC<EvoIconProps> = (p) => (
  // Vault door with a small bar chart
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="3" />
    <path d="M9 8v6" />
    <path d="M6 11h6" />
    <path d="M15 14v-2" />
    <path d="M17.5 14v-4" />
    <path d="M20 14V8" />
    <path d="M5 18v2" />
    <path d="M19 18v2" />
  </Svg>
);

export const GovernmentIcon: React.FC<EvoIconProps> = (p) => (
  // Three pillars with roof
  <Svg {...p}>
    <path d="M3 8l9-5 9 5" />
    <path d="M3 8h18" />
    <path d="M3 21h18" />
    <path d="M5 18h14" />
    <path d="M6 11v7" />
    <path d="M10 11v7" />
    <path d="M14 11v7" />
    <path d="M18 11v7" />
  </Svg>
);

export const TrainingIndustryIcon: React.FC<EvoIconProps> = (p) => (
  // Steps ascending with a flag at the top
  <Svg {...p}>
    <path d="M3 20h4v-4h4v-4h4v-4h4" />
    <path d="M19 8V3" />
    <path d="M19 3l4 1.5L19 6.5" />
  </Svg>
);

/* ───────────────────────────  COMMUNICATION  ──────────────────────────── */

export const ChatIcon: React.FC<EvoIconProps> = (p) => (
  // Speech bubble with signal dots
  <Svg {...p}>
    <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4 3v-3H6a2 2 0 0 1-2-2z" />
    <circle cx="9" cy="10.5" r="0.6" fill="currentColor" />
    <circle cx="12" cy="10.5" r="0.6" fill="currentColor" />
    <circle cx="15" cy="10.5" r="0.6" fill="currentColor" />
  </Svg>
);

export const MeetingsIcon: React.FC<EvoIconProps> = (p) => (
  // Video camera with two people heads
  <Svg {...p}>
    <rect x="2.5" y="8" width="13" height="8" rx="1.5" />
    <path d="M15.5 11l5-2.5v7l-5-2.5z" />
    <circle cx="6" cy="11" r="1" />
    <circle cx="10" cy="11" r="1" />
    <path d="M4.5 14.5c.5-.8 1.3-1.2 2.5-1.2s2 .4 2.5 1.2" />
  </Svg>
);

export const CalendarIcon: React.FC<EvoIconProps> = (p) => (
  // Calendar grid with one highlighted cell
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <rect x="13" y="13" width="4" height="4" rx="0.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const NotificationIcon: React.FC<EvoIconProps> = (p) => (
  // Bell with a pulse arc
  <Svg {...p}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
    <path d="M19.5 5.5c1 1 1.5 2.4 1.5 4" />
    <path d="M4.5 5.5c-1 1-1.5 2.4-1.5 4" />
  </Svg>
);

/* ──────────────────────────────  GENERAL  ─────────────────────────────── */

export const SecurityIcon: React.FC<EvoIconProps> = (p) => (
  // Shield with a padlock
  <Svg {...p}>
    <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
    <rect x="9.25" y="11" width="5.5" height="5" rx="0.75" />
    <path d="M10.5 11V9.5a1.5 1.5 0 0 1 3 0V11" />
  </Svg>
);

export const SettingsIcon: React.FC<EvoIconProps> = (p) => (
  // Gear combined with horizontal sliders
  <Svg {...p}>
    <circle cx="8" cy="8" r="2.25" />
    <path d="M8 4v1" />
    <path d="M8 11v1" />
    <path d="M4 8h1" />
    <path d="M11 8h1" />
    <path d="M5.5 5.5l.7.7" />
    <path d="M9.8 9.8l.7.7" />
    <path d="M5.5 10.5l.7-.7" />
    <path d="M9.8 6.2l.7-.7" />
    <path d="M3 16h12" />
    <path d="M3 20h18" />
    <circle cx="16" cy="16" r="1.5" />
    <circle cx="9" cy="20" r="1.5" />
  </Svg>
);

export const AnalyticsIcon: React.FC<EvoIconProps> = (p) => (
  // Dashboard frame with a bar chart and a line
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18" />
    <path d="M7 16v-3" />
    <path d="M10.5 16v-5" />
    <path d="M14 16v-2" />
    <path d="M17.5 16v-4" />
    <path d="M6 7h.01" />
    <path d="M9 7h.01" />
  </Svg>
);

export const APIIcon: React.FC<EvoIconProps> = (p) => (
  // Curly braces with a connection dot in the middle
  <Svg {...p}>
    <path d="M9 4c-2 0-3 1-3 3v2c0 1.5-1 2-2 2v2c1 0 2 .5 2 2v2c0 2 1 3 3 3" />
    <path d="M15 4c2 0 3 1 3 3v2c0 1.5 1 2 2 2v2c-1 0-2 .5-2 2v2c0 2-1 3-3 3" />
    <circle cx="12" cy="12" r="1.25" fill="currentColor" />
  </Svg>
);

export const GlobalIcon: React.FC<EvoIconProps> = (p) => (
  // Globe with connection nodes
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.5 2.5 3.75 5.5 3.75 8.5s-1.25 6-3.75 8.5" />
    <path d="M12 3.5c-2.5 2.5-3.75 5.5-3.75 8.5s1.25 6 3.75 8.5" />
    <circle cx="6" cy="7" r="0.75" fill="currentColor" />
    <circle cx="18" cy="17" r="0.75" fill="currentColor" />
  </Svg>
);

export const SearchIcon: React.FC<EvoIconProps> = (p) => (
  // Lens with an inner focus ring
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <circle cx="10.5" cy="10.5" r="2.75" />
    <path d="M15.5 15.5L20 20" />
  </Svg>
);

export const SuccessIcon: React.FC<EvoIconProps> = (p) => (
  // Circle with a check
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M7.5 12.5l3 3 6-6.5" />
  </Svg>
);

export const ErrorIcon: React.FC<EvoIconProps> = (p) => (
  // Circle with an X
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6" />
    <path d="M15 9l-6 6" />
  </Svg>
);

export const WarningIcon: React.FC<EvoIconProps> = (p) => (
  // Triangle with exclamation
  <Svg {...p}>
    <path d="M12 3.5l9.5 16.5H2.5z" />
    <path d="M12 10v4.5" />
    <circle cx="12" cy="17" r="0.85" fill="currentColor" />
  </Svg>
);

/* ─────────────────────────────  REGISTRY  ─────────────────────────────── */

export const EvoIconsRegistry = {
  // Platform / Navigation
  EvoHRIcon, EvoBusinessIcon, EvoHospitalityIcon, EvoAIIcon,
  // HR & People
  EmployeesIcon, OrgChartIcon, OnboardingIcon, PerformanceIcon,
  RecruitmentIcon, TrainingIcon, AttendanceIcon, LeavesIcon, PayrollIcon,
  // Business
  CRMIcon, FinanceIcon, TasksIcon, DocumentsIcon, ApprovalsIcon, LogisticsIcon,
  // Hospitality
  HotelIcon, RestaurantIcon, ReservationsIcon, HousekeepingIcon, FrontDeskIcon, KitchenIcon,
  // Industries
  HealthcareIcon, RetailIcon, TechnologyIcon, FinancialServicesIcon, GovernmentIcon, TrainingIndustryIcon,
  // Communication
  ChatIcon, MeetingsIcon, CalendarIcon, NotificationIcon,
  // General
  SecurityIcon, SettingsIcon, AnalyticsIcon, APIIcon, GlobalIcon, SearchIcon,
  SuccessIcon, ErrorIcon, WarningIcon,
} as const;

export type EvoIconName = keyof typeof EvoIconsRegistry;

/**
 * Generic resolver — render any icon by name.
 *   <EvoIcon name="EmployeesIcon" className="text-primary" />
 */
export const EvoIcon: React.FC<EvoIconProps & { name: EvoIconName }> = ({ name, ...rest }) => {
  const Cmp = EvoIconsRegistry[name];
  if (!Cmp) return null;
  return <Cmp {...rest} />;
};

export default EvoIconsRegistry;
